import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  VehicleApi,
  VehicleMakeMetadata,
  VehicleModelMetadata
} from '@/shared/api/vehicles'

interface UseVehicleOnboardingOptions {
  vehicleApi: VehicleApi
}

interface UseVehicleOnboardingState {
  makeOptions: VehicleMakeMetadata[]
  modelOptions: VehicleModelMetadata[]
  selectedMake: string
  selectedModel: string
  year: string
  isLoadingMakes: boolean
  isLoadingModels: boolean
  error?: string
  setMake: (make: string) => void
  setModel: (model: string) => void
  setYear: (value: string) => void
  canSubmit: boolean
  clearError: () => void
}

const collator = new Intl.Collator('en', { sensitivity: 'base' })

function sortByName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => collator.compare(a.name, b.name))
}

export function useVehicleOnboarding({ vehicleApi }: UseVehicleOnboardingOptions): UseVehicleOnboardingState {
  const [makeOptions, setMakeOptions] = useState<VehicleMakeMetadata[]>([])
  const [modelOptions, setModelOptions] = useState<VehicleModelMetadata[]>([])
  const [selectedMake, setSelectedMake] = useState('')
  const [selectedModel, setSelectedModel] = useState('')
  const [year, setYear] = useState('')
  const [isLoadingMakes, setIsLoadingMakes] = useState(false)
  const [isLoadingModels, setIsLoadingModels] = useState(false)
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    let isMounted = true
    async function loadMakes() {
      setIsLoadingMakes(true)
      setError(undefined)
      try {
        const makes = await vehicleApi.getVehicleMakes()
        if (isMounted) {
          setMakeOptions(sortByName(makes))
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unable to load vehicle makes.')
        }
      } finally {
        if (isMounted) {
          setIsLoadingMakes(false)
        }
      }
    }

    loadMakes()
    return () => {
      isMounted = false
    }
  }, [vehicleApi])

  useEffect(() => {
    if (!selectedMake) {
      setModelOptions([])
      setSelectedModel('')
      return
    }

    let isMounted = true
    async function loadModels() {
      setIsLoadingModels(true)
      setError(undefined)
      try {
        const models = await vehicleApi.getVehicleModels({ make: selectedMake, year })
        if (isMounted) {
          setModelOptions(sortByName(models))
          // Reset model selection if current one is no longer present
          if (selectedModel && !models.some(model => model.name === selectedModel)) {
            setSelectedModel('')
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unable to load vehicle models.')
        }
      } finally {
        if (isMounted) {
          setIsLoadingModels(false)
        }
      }
    }

    loadModels()
    return () => {
      isMounted = false
    }
  }, [selectedMake, vehicleApi, year, selectedModel])

  const setMake = useCallback((make: string) => {
    setSelectedMake(make)
    setSelectedModel('')
  }, [])

  const setModel = useCallback((model: string) => {
    setSelectedModel(model)
  }, [])

  const handleYearChange = useCallback((value: string) => {
    const sanitized = value.replace(/[^0-9]/g, '').slice(0, 4)
    setYear(sanitized)
  }, [])

  const canSubmit = useMemo(() => {
    if (!selectedMake || !selectedModel) {
      return false
    }

    if (!/^\d{4}$/.test(year)) {
      return false
    }

    const numericYear = Number(year)
    const currentYear = new Date().getFullYear() + 1
    return numericYear >= 1970 && numericYear <= currentYear
  }, [selectedMake, selectedModel, year])

  const clearError = useCallback(() => setError(undefined), [])

  return {
    makeOptions,
    modelOptions,
    selectedMake,
    selectedModel,
    year,
    isLoadingMakes,
    isLoadingModels,
    error,
    setMake,
    setModel,
    setYear: handleYearChange,
    canSubmit,
    clearError
  }
}

